/**
 * CURATION-E1b-1 / DEL-B — shared ⋯ menu body for OverlayStyleCard + OverlayFlowChain.
 */

import type { MouseEvent } from "react";
import { Check } from "lucide-react";
import type { HubCollection } from "@/lib/fetchStyles";
import { folderLabel } from "@/features/footwear-studio/homeFolders";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export type StyleActionsMenuProps = {
  collections: HubCollection[];
  currentCollectionId?: string | null;
  writeBusy?: boolean;
  onMove: (collectionId: string) => void;
  onNewCollection?: () => void;
  onRename?: () => void;
  /** DEL-B — soft-delete (opens confirm dialog in parent). */
  onDelete?: () => void;
  /** Card uses "Delete"; style window uses "Delete style". */
  deleteLabel?: string;
  /** Library Recently deleted pill — Restore only (no Move/Rename/Delete). */
  restoreOnly?: boolean;
  onRestore?: () => void;
  align?: "start" | "center" | "end";
  onContentClick?: (e: MouseEvent) => void;
};

export function StyleActionsMenu({
  collections,
  currentCollectionId,
  writeBusy,
  onMove,
  onNewCollection,
  onRename,
  onDelete,
  deleteLabel = "Delete",
  restoreOnly = false,
  onRestore,
  align = "end",
  onContentClick,
}: StyleActionsMenuProps) {
  if (restoreOnly) {
    return (
      <DropdownMenuContent
        align={align}
        className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
        onClick={onContentClick}
      >
        <DropdownMenuItem
          disabled={!!writeBusy || !onRestore}
          className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
          onSelect={() => onRestore?.()}
        >
          Restore
        </DropdownMenuItem>
      </DropdownMenuContent>
    );
  }

  return (
    <DropdownMenuContent
      align={align}
      className="min-w-[180px] rounded-[10px] border border-white/10 bg-bgSurface py-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
      onClick={onContentClick}
    >
      <div className="px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-textMuted">
        Move to collection
      </div>
      {collections.map((c) => {
        const current = c.id === currentCollectionId;
        return (
          <DropdownMenuItem
            key={c.id}
            disabled={!!writeBusy || current}
            className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
            onSelect={() => {
              if (current) return;
              onMove(c.id);
            }}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{folderLabel(c)}</span>
              {current ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-[#5cc8f5]" />
              ) : null}
            </span>
          </DropdownMenuItem>
        );
      })}
      {onNewCollection ? (
        <DropdownMenuItem
          disabled={!!writeBusy}
          className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
          onSelect={() => onNewCollection()}
        >
          + New collection…
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuSeparator className="bg-white/10" />
      {onRename ? (
        <DropdownMenuItem
          disabled={!!writeBusy}
          className="cursor-pointer px-2.5 py-1.5 text-[12px] text-white/80 focus:bg-[rgba(56,189,248,0.14)] focus:text-[#5cc8f5]"
          onSelect={() => onRename()}
        >
          Rename
        </DropdownMenuItem>
      ) : null}
      {onDelete ? (
        <DropdownMenuItem
          disabled={!!writeBusy}
          className="cursor-pointer px-2.5 py-1.5 text-[12px] text-rose-300 focus:bg-rose-400/15 focus:text-rose-200"
          onSelect={() => onDelete()}
        >
          {deleteLabel}
        </DropdownMenuItem>
      ) : null}
    </DropdownMenuContent>
  );
}
